import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Stack, styled, Typography } from '@mui/material'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { COLORS, ICONS, remUnit, unit } from '@hcr/ui'
import { isNotNull } from '@hcr/utils'

import { useHoliday } from '../../../contexts'
import { useAccommodationDetailsQuery, useTimeshareIntroductionInfoQuery, useIdToken } from '../../../hooks'
import { Path } from '../../../models'
import { isFlyAccommodation, to } from '../../../utils'

import { TimeshareIntroductionBoxLoading } from './TimeshareIntroductionBoxLoading'

export const TimeshareIntroductionBox: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const idToken = useIdToken()
  const holiday = useHoliday()

  const accommodationDetails = useAccommodationDetailsQuery(
    { idToken: String(idToken), accommodationId: Number(holiday.data.accommodationId) },
    { enabled: isNotNull(holiday.data.accommodationId) && isNotNull(idToken) }
  )

  const hasTimeshareIntroductionInfo =
    accommodationDetails.isSuccess && accommodationDetails.data && isFlyAccommodation(accommodationDetails.data)

  const timeshareIntroductionInfo = useTimeshareIntroductionInfoQuery({
    enabled: isNotNull(idToken) && hasTimeshareIntroductionInfo,
  })

  if (!hasTimeshareIntroductionInfo) {
    return null
  }

  const onClick = () => {
    return navigate(to(Path.BookingTimeshareIntroduction))
  }

  if (holiday.isSuccess && timeshareIntroductionInfo.isSuccess) {
    return (
      <Stack gap={unit(1.5)} marginY={unit(4)}>
        <Typography component='h2' variant='uppercaseL'>
          {t('booking-timeshare-introduction.appointments')}
        </Typography>

        <Container onClick={onClick}>
          <Stack>
            <Typography component='h3' variant='headlineExtraSmall'>
              {timeshareIntroductionInfo.data.title}
            </Typography>
            <Typography component='h4' variant='bodyM' paddingTop={unit(4)}>
              {timeshareIntroductionInfo.data.ingress}
            </Typography>
          </Stack>
          <FontAwesomeIcon icon={ICONS.farChevronRight} fontSize={remUnit(5.5)} color={COLORS.black} />
        </Container>
      </Stack>
    )
  }

  return <TimeshareIntroductionBoxLoading />
}

const Container = styled(Button)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${unit(4)};
  padding: ${unit(5)} ${unit(4)};
  text-align: left;
  background-color: ${COLORS.white};
  color: ${COLORS.black};
  font-weight: normal;
` as typeof Button
